import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Grid,
} from "@mui/material";
import { Person, Business, HomeWork, Gavel } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { ThemeModeProvider } from "../components/ThemeModeContext";
import ThemeProviderWrapper from "../components/ThemeProviderWrapper";
import FeedbackButton from "../components/FeedbackButton";
import axiosInstance from "../Services/axiosConfig";

// Formatação de telefone e CNPJ (exemplos)
const formatPhone = (value) => {
  const numericValue = value.replace(/\D/g, "");
  if (numericValue.length <= 10) {
    return numericValue
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 14);
  } else {
    return numericValue
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .slice(0, 15);
  }
};

const formatCNPJ = (value) => {
  return value
    .replace(/\D/g, "")
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{4})(\d)/, "$1/$2-$3");
};

// Ícones das etapas
const stepIcons = [
  <Person sx={{ fontSize: 36 }} />,
  <Business sx={{ fontSize: 36 }} />,
  <HomeWork sx={{ fontSize: 36 }} />,
  <Gavel sx={{ fontSize: 36 }} />,
];

// Labels das etapas
const stepLabels = ["Conta", "Empresa", "Endereço", "Termos"];

const CompanyRegisterPage = () => {
  const theme = useTheme();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    name: "",
    cnpj: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    address: "",
  });

  // ---------------------------------------------------------------------------
  // Lidando com mudanças (incluindo formatação)
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "phone") {
      newValue = formatPhone(value);
    } else if (name === "cnpj") {
      newValue = formatCNPJ(value);
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  // ---------------------------------------------------------------------------
  // Validação simples por step
  const validateStep = () => {
    setErrorMessage("");
    // Step 0: Email, Password, Phone
    if (step === 0) {
      if (!formData.email || !formData.password || !formData.phone) {
        setErrorMessage("Email, password, and phone are required.");
        return false;
      }
    }
    // Step 1: Contact Name, CNPJ
    if (step === 1) {
      if (!formData.name || !formData.cnpj) {
        setErrorMessage("Contact name and CNPJ are required.");
        return false;
      }
    }
    // Step 2: Endereço
    if (step === 2) {
      if (
        !formData.street ||
        !formData.number ||
        !formData.neighborhood ||
        !formData.city ||
        !formData.state ||
        !formData.zip_code
      ) {
        setErrorMessage("Please fill all address fields.");
        return false;
      }
    }
    // formatar o address para enviar
    setFormData((prev) => ({
      ...prev,
      address: `${prev.street}, ${prev.number}${
        prev.complement ? `, ${prev.complement}` : ""
      }, ${prev.neighborhood}, ${prev.city}, ${prev.state}, ${prev.zip_code}`,
    }));  // <== Adicionado
    // Step 3: Terms
    if (step === 3) {
      if (!agreedToTerms) {
        setErrorMessage("Você precisa concordar com os termos.");
        return false;
      }
    }
    return true;
  };

  // ---------------------------------------------------------------------------
  // Navegação
  const handleNextStep = () => {
    if (!validateStep()) return;
    setStep((prev) => prev + 1);
  };
  const handlePreviousStep = () => {
    setErrorMessage("");
    setStep((prev) => prev - 1);
  };

  // ---------------------------------------------------------------------------
  // Finalizar (na última etapa)
  const handleRegister = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setErrorMessage("");

    // Montando objeto final (exemplo, concatena street e number etc.)
    const finalData = {
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      name: formData.name,
      cnpj: formData.cnpj,
      address: `${formData.street}, ${formData.number}${
        formData.complement ? `, ${formData.complement}` : ""
      }, ${formData.neighborhood}`,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
    };

    try {
      const response = await axiosInstance.post("company-register/", finalData);
      if (response.data.status === "success") {
        alert("A empresa foi registrada com sucesso! Pedimos que aguarde a aprovação do administrador. Caso tenha alguma dúvida, entre em contato conosco. TELEFONE: (11) 98089-6777");
        window.location.href = "/login";
      } else {
        setErrorMessage(response.data.message || "Um erro ocorreu. Por favor, tente novamente.");
      }
    } catch (error) {
      setErrorMessage("Um erro ocorreu. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Conteúdo de cada step
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              label="E-mail"
              name="email"
              type="email"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              label="Senha"
              name="password"
              type="password"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.password}
              onChange={handleChange}
            />
            <TextField
              label="Telefone"
              name="phone"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(11) 99999-9999"
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <TextField
              label="Nome da Empresa"
              name="name"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              label="CNPJ"
              name="cnpj"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.cnpj}
              onChange={handleChange}
              placeholder="12.345.678/0001-90"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Rua"
                  name="street"
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  value={formData.street}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Número"
                  name="number"
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  value={formData.number}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <TextField
              label="Complemento"
              name="complement"
              variant="outlined"
              fullWidth
              margin="normal"
              value={formData.complement}
              onChange={handleChange}
            />

            <TextField
              label="Bairro"
              name="neighborhood"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.neighborhood}
              onChange={handleChange}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Cidade"
                  name="city"
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Estado"
                  name="state"
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <TextField
              label="CEP"
              name="zip_code"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={formData.zip_code}
              onChange={handleChange}
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="body1" align="left" sx={{ mb: 2 }}>
              Ao clicar em "Finalizar", você concorda com os nossos{" "}
              <Link href="terms" underline="hover">
                Termos de uso e Serviço
              </Link>{" "}
              e com a nossa politica de privacidade.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  color="primary"
                />
              }
              label="Eu concordo com os termos"
            />
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="error">
            Erro: Etapa desconhecida.
          </Typography>
        );
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <ThemeModeProvider>
      <ThemeProviderWrapper>
        <Box
          // O background agora depende do mode:
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            // BG antigo no light mode; algo escuro no dark mode
            background:
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`
                : `linear-gradient(135deg, #121212 30%, #2A2A2A 90%)`,
            color: "text.primary",
            p: 4,
            overflow: "hidden",
          }}
        >
          {/* Bolhas (opcional) */}
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              top: -50,
              left: -50,
              animation: "move-1 10s infinite alternate ease-in-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 400,
              height: 400,
              borderRadius: "50%",
              background:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              bottom: -100,
              right: -100,
              animation: "move-2 12s infinite alternate ease-in-out",
            }}
          />

          {/* Card */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 650,
              bgcolor: "background.paper",
              color: "text.primary",
              borderRadius: 3,
              boxShadow: 4,
              p: 4,
              textAlign: "center",
            }}
          >
            {/* Cabeçalho */}
            <Box display="flex" justifyContent="center" mb={2}>
              {stepIcons[step]}
            </Box>
            <Typography variant="h4" gutterBottom>
              Registro de Empresa
            </Typography>

            {/* Etapas (ícones e labels) */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mb={2}
              gap={3}
              flexWrap="wrap"
            >
              {stepLabels.map((label, index) => (
                <Box
                  key={label}
                  textAlign="center"
                  sx={{
                    opacity: index <= step ? 1 : 0.5,
                    cursor: "default",
                  }}
                >
                  {stepIcons[index]}
                  <Typography variant="caption">{label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Erro */}
            {errorMessage && (
              <Typography variant="body2" color="error" gutterBottom>
                {errorMessage}
              </Typography>
            )}

            {/* Conteúdo da etapa */}
            {renderStepContent()}

            {/* Navegação */}
            <Box mt={3} display="flex" justifyContent="space-between">
              {/* Botão Back (oculto no step 0) */}
              {step > 0 && (
                <Button
                  variant="outlined"
                  onClick={handlePreviousStep}
                  disabled={loading}
                >
                  Anterior
                </Button>
              )}

              {/* Último step -> Finish, senão -> Next */}
              {step < 3 ? (
                <Button variant="contained" onClick={handleNextStep} disabled={loading}>
                  Próximo
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRegister}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Finalizar"}
                </Button>
              )}
            </Box>
          </Box>

          <FeedbackButton />

          {/* Keyframes */}
          <style>
            {`
              @keyframes move-1 {
                0% { transform: translate(0, 0); }
                100% { transform: translate(50px, -50px); }
              }
              @keyframes move-2 {
                0% { transform: translate(0, 0); }
                100% { transform: translate(-50px, 50px); }
              }
            `}
          </style>
        </Box>
      </ThemeProviderWrapper>
    </ThemeModeProvider>
  );
};

export default CompanyRegisterPage;
